export interface ApiResponse {
  error?: string
  challengeName?: string
  sub?: string
}

export interface AuthInfo {
  sub: string
  // Cognito User Pool 内の Username。フェデレーションユーザーでは
  // `<provider>_<external_id>` 形式（例: `google_11690...`）で sub と一致しない (#600)。
  // refresh 時の SECRET_HASH 計算に必要なのでサーバから受け取った値をそのままエコーバックする。
  cognitoUsername: string
  exp: number
  idToken: string
  accessToken: string
  refreshToken: string
}

interface ChildUserInfo {
  userId: string
  username: string
  name: string
}

/**
 * Login user information
 */
export interface UserInfo {
  userId: string
  name: string
  email: string
  exp: number
  likes: number
  title: string
  advisorId: string
  isChild: boolean
  /** 羽根筆を使えるか（子アカウントは保護者が ON にしたときだけ。#944） */
  canUseQuill?: boolean
  /** 作品を公開できるか（子アカウントは保護者が ON にしたときだけ。#975） */
  canCrystallize?: boolean
  children?: ChildUserInfo[]

  mpCapacity: number
  mpEnergy: number

  stamps: number

  privileges: string[]

  /** Admin / support gate flag (#622). True iff the user is in CONSULT_SUPPORT group. */
  isConsultSupport?: boolean

  /** Editorial-staff gate flag (#708). True iff the user is in EDITORIAL group.
   *  ゆりかごの「冒険者のクリスタル」タブの表示可否に使う。 */
  isEditorial?: boolean

  /** Creator-page slug (#417). null/undefined = no public /c/<slug> page yet. */
  slug?: string | null

  /** Last slug change timestamp (ISO 8601). 30 日クールダウン判定に使う。 */
  slugChangedAt?: string | null
}

/**
 * Definition of an area.
 */
export interface SceneInfo {
  sceneId: string
  // order: number

  // type: string
  name: string
  description: string

  // expToUnlock: number
  // expEarned: number

  disabled: boolean
  isVisible: boolean
  isMarked: boolean
  isUnlocked: boolean
  isPurchased: boolean
  isCleared: boolean
  // conditionToUnlock: string

  pageSlug?: string
  // sellers?: Array<string>
  // priceId?: string
  // price?: number

  parentId?: string

  // entryPoint?: string

  enableGoddessJudgement?: boolean

  coordX: number
  coordY: number

  // #721: ワンダーのデモ動画（YouTube）ID。モバイルナビの画像付きリストで
  // 代表画像（サムネイル）に使う。動画のないシーンは undefined。
  videoId?: string
}

/**
 * Login user information
 */
// export interface PotInfo {
//   capacity: number
//   energy: number
// }

/**
 * 冒険の手記の 1 ページ（#900）。
 *
 * **未獲得なら `acquired` 以外ほぼ空**（サーバが載せない）。`areaId` すら無い ——
 * 未公開エリアの id はそれ自体が物語のネタバレなので渡さない。`memoirId` は v-for の
 * key にするための連番で、中身を持たない。
 */
export interface MemoirInfo {
  memoirId: string
  areaId?: string | null
  acquired: boolean
  title?: string | null
  image?: string | null
  eventId?: string | null
  /** そのエリアのワンダーでもらえる経験値の合計と、そのうち得た分（#971。見えているエリアだけ） */
  expTotal?: number | null
  expEarned?: number | null
}

export interface MemoirsInfo {
  /** メニューの NEW 印（`story_memoir:UPDATED`）。一覧を開くと下りる。 */
  updated: boolean
  items: MemoirInfo[]
}

export interface GetCodeResponse {
  code: string
}

export interface WonderInfo {
  id: string
  name: string
  description: string
  isUnlocked: boolean
  code: string
}

export interface AuthChallengeSession {
  challengeName: string
  session?: string
  username?: string
  password?: string
}

export enum AuthMode {
  SIGNUP = 'signup',
  CONFIRM = 'confirm',
  RESEND = 'resend',
  FORGOTPW = 'forgotpw',
  RESETPW = 'resetpw',
  SETNEWPW = 'setnewpw',
  SIGNIN = 'signin',
  OFFLINE = 'offline',
}

export type SpeakerId = 'system' | 'senpai' | 'fairly' | 'goddess' | 'broker' | 'magician' | 'video' | 'image' | 'skills'
export type HintTypeId = 'toast' | 'bubble' | 'splotlight' | 'arrow'
export type EffectId = 'none' | 'flash' | 'glow'
export type SoundId = 'submit' | 'cancel' | 'click' | 'learned'
export type ButtonId = 'submit' | 'cancel' | 'middle'
export type HtmlElementId = string
export type GameFlag = string // Enum value で文字列として渡ってくるので string 型

export type Hint = {
  type: HintTypeId
  text?: string
  target?: HtmlElementId
  duration?: number
}

type NextAction = {
  label: string
  disabled: boolean
  sound: SoundId
  eventId?: string
  effect?: EffectId
}

interface BaseStep {
  flagUpdates?: Record<GameFlag, boolean>
  sound?: SoundId
  effect?: EffectId
  hint?: Hint
  only?: Array<string>
}

export interface RatingTarget {
  // Phase 1 では "advisor_response" のみ。後続で "work" "wonder_clear" 等を追加 (#654)
  targetType: 'advisor_response'
  targetId: string
  context?: Record<string, string> | null
}

/**
 * ダイアログの器（#898）。`large` は 4:3 いっぱいの LargeDialog で出す。
 * 未指定は従来どおり通常サイズの Dialog。
 */
export type DialogSize = 'default' | 'large'

export interface DialogStep extends BaseStep {
  type: 'dialog'
  speaker: SpeakerId
  title: string
  text: string
  image?: string
  music?: string
  size?: DialogSize
  actions: Partial<Record<ButtonId, NextAction>>
  // set されているとき、ダイアログ下に 👍/🤔/👎 評価ボタンを描画する (#654)
  ratingTarget?: RatingTarget | null
}

export interface ChoiceStep extends BaseStep {
  type: 'choice'
  speaker: SpeakerId
  title: string
  text: string
  choices: NextAction[]
  actions: Partial<Record<ButtonId, NextAction>>
}

export interface ChatStep extends BaseStep {
  type: 'chat'
  speaker: SpeakerId
  text: string
  choices: NextAction[]
}

export interface NagivateStep extends BaseStep {
  type: 'navigate'
  target: string
}

export interface CustomStep extends BaseStep {
  type: 'custom'
  operation?: string
  sceneId?: string
}

// #791 MR6: 非同期タスクの進行を表す step。sfn mode の create_work が返す。
// client は taskId で GET /api/v1/tasks/{taskId} を polling し、terminal state
// (succeeded / publish_event_failed / failed) を検知したら response の
// onSuccess / onFailure DialogStep を event queue の先頭に差し込む。
// 詳細は doc/architecture/async_workflows.md 参照。
export interface TaskStep extends BaseStep {
  type: 'task'
  taskId: string
}

// #791 MR6: Task API response の TaskInfo。abstract enum で SFN state 名は leak しない。
export type TaskState
  = | 'pending'
    | 'validating'
    | 'building'
    | 'publishing'
    | 'finalizing'
    | 'succeeded'
    | 'publish_event_failed'
    | 'failed'

export interface TaskInfo {
  taskId: string
  state: TaskState
  ceremonyLabel: string
  result?: Record<string, unknown> | null
  error?: { code: string, message: string } | null
  onSuccess?: DialogStep | null
  onFailure?: DialogStep | null
}

export type EventStep = DialogStep | ChoiceStep | ChatStep | NagivateStep | CustomStep | TaskStep

export interface EventResult {
  steps: EventStep[]
}

export interface CreateCheckoutSessionRequest {
  priceId: string
  returnUrl: string
  quantity?: number
  mode?: 'payment' | 'subscription'
  requireShipping?: boolean
  customerEmail?: string
  userId?: string
  sceneId?: string
}

// E2Eテスト用: window.__eventSystem の型定義
declare global {
  interface Window {
    __eventSystem?: {
      isProcessing: import('vue').ComputedRef<boolean>
      lastProcessedSteps: import('vue').ComputedRef<EventStep[]>
    }
  }
}

export interface GrimoireItem {
  id: string
  type: string
  name: string
  readonly: boolean
  description: string
  createdAt: string
  updatedAt?: string
}
